import { useEffect, useState } from "react";
import { supAPI } from "./supApi";
import { toast } from "sonner";
import { Save, Check, Info } from "lucide-react";

export default function SupplySettings() {
  const [cats, setCats] = useState([]);
  const [enabled, setEnabled] = useState(new Set());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [all, cfg] = await Promise.all([supAPI.categories(), supAPI.config()]);
    setCats(all.filter(c => c.is_system));
    setEnabled(new Set(cfg.enabled_categories || []));
  };
  useEffect(() => { load(); }, []);

  const toggle = (code) => {
    const next = new Set(enabled);
    next.has(code) ? next.delete(code) : next.add(code);
    setEnabled(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await supAPI.setEnabledCategories(Array.from(enabled));
      toast.success("Category modules updated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5 max-w-3xl" data-testid="sup-settings">
      <div>
        <h2 className="font-display text-[16px] font-semibold text-hocp-navy">Category Modules</h2>
        <p className="text-[12.5px] text-slate-500 mt-1">
          Enable only the supply categories your hotel actually manages. Items, PRs and reports will be scoped to enabled categories.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
        {cats.map(c => {
          const on = enabled.has(c.code);
          return (
            <button key={c.id} onClick={() => toggle(c.code)} data-testid={`sup-cat-toggle-${c.code}`}
                    className={`text-left border rounded-xl px-4 py-3.5 transition-all flex items-center gap-3 ${
                      on ? "border-hocp-navy bg-hocp-navy/[0.03] shadow-sm" : "border-slate-200 hover:border-slate-300"}`}>
              <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 ${
                on ? "border-hocp-navy bg-hocp-navy" : "border-slate-300"}`}>
                {on && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3}/>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13.5px] font-medium text-hocp-navy">{c.name}</div>
                <div className="text-[11.5px] mono text-slate-400">{c.code}</div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex items-start gap-2 bg-sky-50/60 border border-sky-100 rounded-xl px-3.5 py-3">
        <Info className="w-4 h-4 text-sky-600 mt-0.5 shrink-0" strokeWidth={1.75}/>
        <div className="text-[12px] text-sky-900">
          Disabling a category won&apos;t delete items already created under it — it just hides the category from new item, PR and reporting pickers.
        </div>
      </div>

      <div>
        <button onClick={save} disabled={saving} data-testid="sup-settings-save"
                className="h-10 px-5 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-medium inline-flex items-center gap-2 disabled:opacity-60">
          <Save className="w-4 h-4"/> {saving ? "Saving…" : "Save configuration"}
        </button>
      </div>
    </div>
  );
}
